import React, { useState, useEffect, useCallback, useMemo } from "react";
import PropTypes from "prop-types";
import { useSelector, useDispatch } from "react-redux";
import { FormCheck } from "react-bootstrap";
import AppModal from "../../common/modal/AppModal";
import ColorPicker from "../../common/color-picker/ColorPicker";
import ToolbarFileProvider from "../../external_provider/file_modal/APIProviders/common/ToolbarFileProvider";
import i18n from "i18next";
import {
  changeThemePalette,
  changeThemePaletteAll,
  changeGlobalConfig,
  updateViewToolbar,
} from "../../../../common/actions";
import { fetchThemes, fetchPalettes, fetchThemeStyle } from "../../../../common/server_utils";
import {
  resolveCustomModeFromToolbar,
  CUSTOM_THEME_ID,
  DEFAULT_BACKGROUND_COLOR,
} from "./resolveCustomModeFromToolbar";
import { sanitizeThemeHtml } from "./sanitizeThemeHtml";
import { HtmlEditMode } from "../../common/html_edit_modal/HtmlEditModal";
import { LuTrash2 } from "react-icons/lu";
import "./_scene_theme_palette.scss";

function buildPalettePreviewBackground(hue) {
  if (typeof hue !== "number") {
    return "linear-gradient(135deg, #111827 0%, #374151 100%)";
  }
  return `linear-gradient(135deg, hsl(${hue} 78% 62%) 0%, hsl(${(hue + 36) % 360} 72% 52%) 100%)`;
}

const ThemePaletteModal = ({ show, onClose, onAddImageModalToggle }) => {
  const dispatch = useDispatch();

  const navItemSelected = useSelector((state) => state.undoGroup.present.navItemSelected);
  const containedViewSelected = useSelector(
    (state) => state.undoGroup.present.containedViewSelected
  );
  const viewToolbars = useSelector((state) => state.undoGroup.present.viewToolbarsById);
  const globalConfig = useSelector((state) => state.undoGroup.present.globalConfig);

  const currentViewId = useMemo(() => {
    return containedViewSelected && containedViewSelected !== 0
      ? containedViewSelected
      : navItemSelected;
  }, [containedViewSelected, navItemSelected]);

  const [themes, setThemes] = useState([]);
  const [palettes, setPalettes] = useState([]);
  const [selectedThemeId, setSelectedThemeId] = useState(CUSTOM_THEME_ID);
  const [selectedPaletteId, setSelectedPaletteId] = useState("");
  const [isLoadingThemes, setIsLoadingThemes] = useState(false);
  const [isLoadingPalettes, setIsLoadingPalettes] = useState(false);
  const [isApplying, setIsApplying] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [applyToAll, setApplyToAll] = useState(false);

  // Custom-mode local state
  const [customMode, setCustomMode] = useState("color");
  const [customImageUrl, setCustomImageUrl] = useState("");
  const [customBackgroundAttr, setCustomBackgroundAttr] = useState("full");
  const [customBackgroundZoom, setCustomBackgroundZoom] = useState(100);
  const [customBackgroundOpacity, setCustomBackgroundOpacity] = useState(100);
  const [customHtml, setCustomHtml] = useState("");
  const [customColor, setCustomColor] = useState(DEFAULT_BACKGROUND_COLOR);

  const isCustom = selectedThemeId === CUSTOM_THEME_ID;

  const loadThemes = useCallback(async () => {
    setIsLoadingThemes(true);
    setErrorMsg("");
    try {
      const themesResp = await fetchThemes();
      if (themesResp && Array.isArray(themesResp)) {
        // Filter out any backend "custom" to avoid duplication with our sentinel.
        setThemes(themesResp.filter((t) => t && t.id !== CUSTOM_THEME_ID));
      } else {
        setThemes([]);
        setErrorMsg(i18n.t("ThemePalette.NoThemesError"));
      }
    } catch (err) {
      console.warn("Failed to load themes", err);
      setThemes([]);
      setErrorMsg(i18n.t("ThemePalette.NoThemesError"));
    } finally {
      setIsLoadingThemes(false);
    }
  }, []);

  const loadPalettes = useCallback(async (themeId) => {
    if (!themeId || themeId === CUSTOM_THEME_ID) {
      setPalettes([]);
      setSelectedPaletteId("");
      return;
    }
    setIsLoadingPalettes(true);
    setErrorMsg("");
    try {
      const palettesResp = await fetchPalettes(themeId);
      if (palettesResp && palettesResp.palettes && Array.isArray(palettesResp.palettes)) {
        setPalettes(palettesResp.palettes);
      } else if (Array.isArray(palettesResp)) {
        setPalettes(palettesResp);
      } else {
        setPalettes([]);
      }
    } catch (err) {
      console.warn("Failed to load palettes", err);
      setPalettes([]);
    } finally {
      setIsLoadingPalettes(false);
    }
  }, []);

  const setInitialSelection = useCallback(() => {
    const globalTheme = globalConfig?.globalTheme;
    const isGlobalApplyToAll = globalTheme?.applyToAll === true;
    setApplyToAll(isGlobalApplyToAll);

    const source = isGlobalApplyToAll ? globalTheme : viewToolbars[currentViewId];
    const resolved = resolveCustomModeFromToolbar(source);

    setSelectedThemeId(resolved.themeIdForSelect);
    setSelectedPaletteId(source?.paletteId || "");
    if (resolved.themeIdForSelect !== CUSTOM_THEME_ID) {
      loadPalettes(resolved.themeIdForSelect);
    } else {
      setPalettes([]);
    }

    setCustomMode(resolved.mode || "color");
    setCustomImageUrl(resolved.imageUrl);
    setCustomBackgroundAttr(resolved.backgroundAttr);
    setCustomBackgroundZoom(resolved.backgroundZoom);
    setCustomBackgroundOpacity(resolved.backgroundOpacity);
    setCustomHtml(resolved.html);
    setCustomColor(resolved.color);
  }, [currentViewId, viewToolbars, globalConfig, loadPalettes]);

  useEffect(() => {
    if (show) {
      loadThemes();
      setInitialSelection();
    }
  }, [show, loadThemes, setInitialSelection]);

  useEffect(() => {
    if (show) {
      setInitialSelection();
    }
  }, [currentViewId, setInitialSelection, show]);

  const onThemeChange = (e) => {
    const themeId = e.target.value;
    setSelectedThemeId(themeId);
    setSelectedPaletteId("");
    setPalettes([]);
    if (themeId && themeId !== CUSTOM_THEME_ID) {
      loadPalettes(themeId);
    }
  };

  const onPaletteChange = (paletteId) => {
    setSelectedPaletteId(paletteId);
  };

  const onCustomColorChange = (e) => {
    if (e && e.color) setCustomColor(e.color);
  };

  const activateImage = (url) => {
    if (url != null) setCustomImageUrl(url);
    if (selectedThemeId !== CUSTOM_THEME_ID) {
      setSelectedThemeId(CUSTOM_THEME_ID);
      setSelectedPaletteId("");
      setPalettes([]);
    }
    setCustomMode("image");
  };

  const handleApply = async () => {
    if (isCustom) {
      // Apply Custom payload in a single dispatch
      let payload = { themeId: CUSTOM_THEME_ID, customMode };
      if (customMode === "image") {
        if (!customImageUrl) {
          setErrorMsg(i18n.t("ThemePalette.ErrorSelectBoth"));
          return;
        }
        payload = {
          ...payload,
          background: customImageUrl,
          backgroundAttr: customBackgroundAttr,
          backgroundZoom: Number(customBackgroundZoom),
          backgroundOpacity: Number(customBackgroundOpacity),
        };
      } else if (customMode === "html") {
        payload = {
          ...payload,
          themeHtml: sanitizeThemeHtml(customHtml),
        };
      } else {
        // color
        payload = {
          ...payload,
          background: customColor,
          backgroundAttr: "full",
          backgroundOpacity: Number(customBackgroundOpacity),
        };
      }
      dispatch(updateViewToolbar(currentViewId, payload));
      // Turn off global applyToAll so Custom is per-scene
      const prevGlobal = globalConfig?.globalTheme;
      if (prevGlobal?.applyToAll) {
        dispatch(changeGlobalConfig("globalTheme", { ...prevGlobal, applyToAll: false }));
      }
      onClose();
      return;
    }

    // Preset theme path (unchanged flow)
    if (!selectedThemeId || !selectedPaletteId) {
      setErrorMsg(i18n.t("ThemePalette.ErrorSelectBoth"));
      return;
    }

    setIsApplying(true);
    setErrorMsg("");
    try {
      let themeHtml = "";
      let themeCssVariables = {};
      try {
        const styleResp = await fetchThemeStyle(selectedThemeId, selectedPaletteId);
        if (styleResp && styleResp.html) {
          themeHtml = styleResp.html;
          themeCssVariables = styleResp.cssVariables || {};
        } else if (typeof styleResp === "string") {
          themeHtml = styleResp;
        }
      } catch (e) {
        console.warn("Failed to fetch theme HTML", e);
        throw new Error(i18n.t("ThemePalette.ThemeStyleFetchError"));
      }

      if (applyToAll) {
        dispatch(
          changeThemePaletteAll(selectedThemeId, selectedPaletteId, themeHtml, themeCssVariables)
        );
        dispatch(
          changeGlobalConfig("globalTheme", {
            themeId: selectedThemeId,
            paletteId: selectedPaletteId,
            applyToAll: true,
            themeHtml,
            themeCssVariables,
          })
        );
      } else {
        dispatch(
          changeThemePalette(
            currentViewId,
            selectedThemeId,
            selectedPaletteId,
            themeHtml,
            themeCssVariables
          )
        );
        const prevGlobal = globalConfig?.globalTheme;
        if (prevGlobal?.applyToAll) {
          dispatch(changeGlobalConfig("globalTheme", { ...prevGlobal, applyToAll: false }));
        }
      }
      onClose();
    } catch (e) {
      setErrorMsg(e.message || i18n.t("ThemePalette.UnknownError"));
    } finally {
      setIsApplying(false);
    }
  };

  const handleRemoveTheme = () => {
    if (applyToAll) {
      dispatch(changeThemePaletteAll("", "", "", {}));
      dispatch(
        changeGlobalConfig("globalTheme", {
          themeId: "",
          paletteId: "",
          applyToAll: false,
          themeHtml: "",
          themeCssVariables: {},
        })
      );
    } else {
      dispatch(changeThemePalette(currentViewId, "", "", "", {}));
    }
    onClose();
  };

  if (!show) return null;

  const applyDisabled =
    isApplying ||
    (isCustom ? customMode === "image" && !customImageUrl : !selectedThemeId || !selectedPaletteId);

  const handleDismiss = () => {
    onClose();
    setErrorMsg("");
    setInitialSelection();
  };

  const footer = (
    <div className="app-modal__footer tp-modal__footer-row">
      <button
        type="button"
        className="app-modal__btn app-modal__btn--danger"
        onClick={handleRemoveTheme}
        disabled={isApplying}
      >
        <LuTrash2 aria-hidden="true" />
        <span>{i18n.t("ThemePalette.RemoveTheme")}</span>
      </button>
      <div className="tp-modal__footer-spacer" />
      <button
        type="button"
        className="app-modal__btn app-modal__btn--ghost"
        onClick={handleDismiss}
        disabled={isApplying}
      >
        {i18n.t("Cancel") || i18n.t("global_config.Discard")}
      </button>
      <button
        type="button"
        className="app-modal__btn app-modal__btn--primary"
        onClick={handleApply}
        disabled={applyDisabled}
      >
        {isApplying ? i18n.t("ThemePalette.Applying") : i18n.t("ThemePalette.Apply")}
      </button>
    </div>
  );

  return (
    <AppModal
      show={show}
      onClose={handleDismiss}
      title={i18n.t("ThemePalette.Title")}
      size="lg"
      className="themeModal tpModal"
      dialogClassName="tp-modal__dialog"
      bodyClassName="tp-modal__body"
      bodyProps={{ className: "tp-modal__body" }}
      footerProps={{ className: "tp-modal__footer" }}
      footer={footer}
    >
      <div className="theme-palette-modal-body">
        <div className="theme-section">
          <label className="section-label">{i18n.t("ThemePalette.Theme")}</label>
          <select
            className="theme-select"
            value={selectedThemeId}
            onChange={onThemeChange}
            disabled={isLoadingThemes || isApplying}
          >
            <option value={CUSTOM_THEME_ID}>{i18n.t("ThemePalette.Custom")}</option>
            {themes.map((theme) => (
              <option key={theme.id} value={theme.id}>
                {theme.name}
              </option>
            ))}
          </select>
          {isLoadingThemes && (
            <p className="loading-text">{i18n.t("ThemePalette.LoadingThemes")}</p>
          )}
        </div>

        {isCustom && (
          <div className="custom-section">
            <label className="section-label">{i18n.t("ThemePalette.Mode")}</label>
            <div className="tp-radio-group custom-mode-radios">
              <FormCheck
                type="radio"
                id="custom-mode-html"
                name="custom-mode"
                label={i18n.t("ThemePalette.ModeHtml")}
                checked={customMode === "html"}
                onChange={() => setCustomMode("html")}
              />
              <FormCheck
                type="radio"
                id="custom-mode-color"
                name="custom-mode"
                label={i18n.t("ThemePalette.ModeColor")}
                checked={customMode === "color"}
                onChange={() => setCustomMode("color")}
              />
            </div>

            {customMode === "html" && (
              <div className="custom-panel custom-html-panel">
                <HtmlEditMode
                  value={customHtml}
                  onChange={setCustomHtml}
                  placeholder={i18n.t("ThemePalette.HtmlPlaceholder")}
                />
              </div>
            )}

            {customMode === "color" && (
              <div className="custom-panel custom-color-panel">
                <ColorPicker value={customColor} onChange={onCustomColorChange} />
              </div>
            )}
          </div>
        )}

        {!isCustom && (
          <div className="palette-section">
            <label className="section-label">{i18n.t("ThemePalette.Palette")}</label>
            <div className="palette-grid">
              {palettes.map((palette) => {
                const isActive = palette.id === selectedPaletteId;
                return (
                  <div
                    key={palette.id}
                    onClick={() => {
                      if (!isApplying) onPaletteChange(palette.id);
                    }}
                    className={`palette-item ${isActive ? "active" : ""} ${isApplying ? "disabled" : ""}`}
                  >
                    <div
                      className="palette-preview"
                      style={{ background: buildPalettePreviewBackground(palette.hue) }}
                    />
                    <p className="palette-title">{palette.name}</p>
                    <p className="palette-subtitle">{palette.id}</p>
                  </div>
                );
              })}
            </div>
            {isLoadingPalettes && (
              <p className="loading-text">{i18n.t("ThemePalette.LoadingPalettes")}</p>
            )}
            {!isLoadingPalettes && palettes.length === 0 && selectedThemeId && (
              <p className="no-palettes-text">{i18n.t("ThemePalette.NoPalettes")}</p>
            )}
          </div>
        )}

        {!isCustom && (
          <div className="apply-to-all-section">
            <label className="apply-to-all-label">
              <input
                type="checkbox"
                className="apply-to-all-checkbox"
                checked={applyToAll}
                onChange={(e) => setApplyToAll(e.target.checked)}
                disabled={isApplying}
              />
              <span>{i18n.t("ThemePalette.ApplyToAll")}</span>
            </label>
          </div>
        )}

        <div className="bg-image-section">
          <label className="section-label">{i18n.t("background.background_image")}</label>
          <div
            className={`custom-panel custom-image-panel${
              isCustom && customMode === "image" ? " is-active" : ""
            }`}
          >
            <ToolbarFileProvider
              id={currentViewId}
              formControlProps={{
                label: i18n.t("background.background_input_url"),
                value: customImageUrl,
                onChange: (e) => {
                  if (e && e.target) activateImage(e.target.value);
                  else if (e && e.value) activateImage(e.value);
                },
              }}
              value={customImageUrl}
              openModal={() => {
                if (onAddImageModalToggle) {
                  onAddImageModalToggle((url) => {
                    if (url) activateImage(url);
                  }, i18n.t("AddImage"));
                }
              }}
              buttontext={i18n.t("importFile.title")}
              accept="image/*"
              onChange={(e) => {
                if (e && e.value) activateImage(e.value);
                else if (e && e.target) activateImage(e.target.value);
              }}
            />

            {customImageUrl && (
              <>
                <div className="tp-radio-group custom-attr-radios">
                  {["full", "repeat", "centered", "fit"].map((attr) => (
                    <FormCheck
                      key={attr}
                      type="radio"
                      id={`custom-attr-${attr}`}
                      name="custom-attr"
                      label={i18n.t(`background.${attr === "full" ? "cover" : attr}`)}
                      checked={customBackgroundAttr === attr}
                      onChange={() => {
                        setCustomBackgroundAttr(attr);
                        activateImage();
                      }}
                    />
                  ))}
                </div>

                {customBackgroundAttr !== "full" && customBackgroundAttr !== "fit" && (
                  <div className="slider-row">
                    <label>
                      {i18n.t("background.background_zoom")} {customBackgroundZoom}%
                    </label>
                    <input
                      type="range"
                      min={1}
                      max={200}
                      value={customBackgroundZoom}
                      onChange={(e) => {
                        setCustomBackgroundZoom(Number(e.target.value));
                        activateImage();
                      }}
                    />
                  </div>
                )}

                <div className="slider-row">
                  <label>
                    {i18n.t("background.background_opacity")} {customBackgroundOpacity}%
                  </label>
                  <input
                    type="range"
                    min={0}
                    max={100}
                    value={customBackgroundOpacity}
                    onChange={(e) => {
                      setCustomBackgroundOpacity(Number(e.target.value));
                      activateImage();
                    }}
                  />
                </div>
              </>
            )}
          </div>
        </div>

        {errorMsg && <div className="error-container">{errorMsg}</div>}
      </div>
    </AppModal>
  );
};

ThemePaletteModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onAddImageModalToggle: PropTypes.func,
};

export default ThemePaletteModal;
